import { useComposedRefs } from "@radix-ui/react-compose-refs";
import {
  ChevronDownIcon,
  DragHandleDots2Icon,
  InfoCircledIcon,
} from "@radix-ui/react-icons";
import { Portal } from "@radix-ui/react-portal";
import { cva } from "class-variance-authority";
import {
  createContext,
  use,
  useCallback,
  useEffect,
  useRef,
  useState,
} from "react";
import { mergeProps, useMove } from "react-aria";
import { useThrottledCallback } from "sanity";
import { Box, Editor, HTMLContainer, useEditor, Vec } from "tldraw";
import { useSharedResizeObserver } from "use-shared-resize-observer";

import { InFrontOfCanvasPortalContext } from "@/components/canvas-editor/InFrontOfCanvasPortal";
import {
  BindableShape,
  BindableUtil,
} from "@/components/canvas-editor/shapes/bindable";
import { Dimensions, Pattern } from "@/components/canvas-editor/shapes/shapes";
import {
  preventBubble,
  ReadonlyEditorContext,
} from "@/components/canvas-editor/utils";
import { useIsMobile } from "@/components/hooks/use-mobile";
import { MarkdownRenderer } from "@/components/MarkdownRenderer";
import { Avatar } from "@/components/ui/avatar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Separator } from "@/components/ui/separator";
import { NodeMetadata, User } from "@/lib/types";
import { cn } from "@/lib/utils";

export const theme = {
  preview: {
    default: "bg-slate-10",
    green: "bg-green-10",
    red: "bg-red-10",
    blue: "bg-blue-10",
    purple: "bg-purple-10",
    teal: "bg-teal-10",
    pink: "bg-pink-10",
    amber: "bg-amber-10",
    plum: "bg-plum-10",
    brown: "bg-brown-10",
    tomato: "bg-tomato-10",
    ruby: "bg-ruby-10",
    crimson: "bg-crimson-10",
    violet: "bg-violet-10",
    iris: "bg-iris-10",
    indigo: "bg-indigo-10",
    cyan: "bg-cyan-10",
    jade: "bg-jade-10",
    grass: "bg-grass-10",
    bronze: "bg-bronze-10",
    gold: "bg-gold-10",
    orange: "bg-orange-10",
    yellow: "bg-yellow-10",
    lime: "bg-lime-10",
    mint: "bg-mint-10",
    sky: "bg-sky-10",
  },
  switch: {
    default: "data-[state=checked]:bg-jade-solid",
    green: "data-[state=checked]:bg-green-solid",
    red: "data-[state=checked]:bg-red-solid",
    blue: "data-[state=checked]:bg-blue-solid",
    purple: "data-[state=checked]:bg-purple-solid",
    teal: "data-[state=checked]:bg-teal-solid",
    pink: "data-[state=checked]:bg-pink-solid",
    amber: "data-[state=checked]:bg-amber-solid",
    plum: "data-[state=checked]:bg-plum-solid",
    brown: "data-[state=checked]:bg-brown-solid",
    tomato: "data-[state=checked]:bg-tomato-solid",
    ruby: "data-[state=checked]:bg-ruby-solid",
    crimson: "data-[state=checked]:bg-crimson-solid",
    violet: "data-[state=checked]:bg-violet-solid",
    iris: "data-[state=checked]:bg-iris-solid",
    indigo: "data-[state=checked]:bg-indigo-solid",
    cyan: "data-[state=checked]:bg-cyan-solid",
    jade: "data-[state=checked]:bg-jade-solid",
    grass: "data-[state=checked]:bg-grass-solid",
    bronze: "data-[state=checked]:bg-bronze-solid",
    gold: "data-[state=checked]:bg-gold-solid",
    orange: "data-[state=checked]:bg-orange-solid",
    yellow: "data-[state=checked]:bg-yellow-solid",
    lime: "data-[state=checked]:bg-lime-solid",
    mint: "data-[state=checked]:bg-mint-solid",
    sky: "data-[state=checked]:bg-sky-solid",
  },
  readOnly: {
    default:
      "bg-slate-3 dark:bg-slatedark-3 border-slate-8 dark:border-slatedark-8 text-slate-normal",
    green:
      "bg-green-3 dark:bg-greendark-3 border-green-8 dark:border-greendark-8 text-green-normal",
    red: "bg-red-3 dark:bg-reddark-3 border-red-8 dark:border-reddark-8 text-red-normal",
    blue: "bg-blue-3 dark:bg-bluedark-3 border-blue-8 dark:border-bluedark-8 text-blue-normal",
    purple:
      "bg-purple-3 dark:bg-purpledark-3 border-purple-8 dark:border-purpledark-8 text-purple-normal",
    teal: "bg-teal-3 dark:bg-tealdark-3 border-teal-8 dark:border-tealdark-8 text-teal-normal",
    pink: "bg-pink-3 dark:bg-pinkdark-3 border-pink-8 dark:border-pinkdark-8 text-pink-normal",
    plum: "bg-plum-3 dark:bg-plumdark-3 border-plum-8 dark:border-plumdark-8 text-plum-normal",
    brown:
      "bg-brown-3 dark:bg-browndark-3 border-brown-8 dark:border-browndark-8 text-brown-normal",
    tomato:
      "bg-tomato-3 dark:bg-tomatodark-3 border-tomato-8 dark:border-tomatodark-8 text-tomato-normal",
    ruby: "bg-ruby-3 dark:bg-rubydark-3 border-ruby-8 dark:border-rubydark-8 text-ruby-normal",
    crimson:
      "bg-crimson-3 dark:bg-crimsondark-3 border-crimson-8 dark:border-crimsondark-8 text-crimson-normal",
    violet:
      "bg-violet-3 dark:bg-violetdark-3 border-violet-8 dark:border-violetdark-8 text-violet-normal",
    iris: "bg-iris-3 dark:bg-irisdark-3 border-iris-8 dark:border-irisdark-8 text-iris-normal",
    indigo:
      "bg-indigo-3 dark:bg-indigodark-3 border-indigo-8 dark:border-indigodark-8 text-indigo-normal",
    cyan: "bg-cyan-3 dark:bg-cyandark-3 border-cyan-8 dark:border-cyandark-8 text-cyan-normal",
    jade: "bg-jade-3 dark:bg-jadedark-3 border-jade-8 dark:border-jadedark-8 text-jade-normal",
    grass:
      "bg-grass-3 dark:bg-grassdark-3 border-grass-8 dark:border-grassdark-8 text-grass-normal",
    bronze:
      "bg-bronze-3 dark:bg-bronzedark-3 border-bronze-8 dark:border-bronzedark-8 text-bronze-normal",
    gold: "bg-gold-3 dark:bg-golddark-3 border-gold-8 dark:border-golddark-8 text-gold-normal",
    orange:
      "bg-orange-3 dark:bg-orangedark-3 border-orange-8 dark:border-orangedark-8 text-orange-normal",

    amber:
      "bg-amber-3 dark:bg-amberdark-3 border-amber-8 dark:border-amberdark-8 text-amber-normal",
    yellow:
      "bg-yellow-3 dark:bg-yellowdark-3 border-yellow-8 dark:border-yellowdark-8 text-yellow-normal",
    lime: "bg-lime-3 dark:bg-limedark-3 border-lime-8 dark:border-limedark-8 text-lime-normal",
    mint: "bg-mint-3 dark:bg-mintdark-3 border-mint-8 dark:border-mintdark-8 text-mint-normal",
    sky: "bg-sky-3 dark:bg-skydark-3 border-sky-8 dark:border-skydark-8 text-sky-normal",
  },
  input: {
    default:
      "border border-slate-8 dark:border-slatedark-8 hover:border-slate-9 dark:hover:border-slatedark-9 bg-slate-subtle",
    green:
      "border border-green-8 dark:border-greendark-8 hover:border-green-9 dark:hover:border-greendark-9 bg-green-subtle",
    red: "border border-red-8 dark:border-reddark-8 hover:border-red-9 dark:hover:border-reddark-9 bg-red-subtle",
    blue: "border border-blue-8 dark:border-bluedark-8 hover:border-blue-9 dark:hover:border-bluedark-9 bg-blue-subtle",
    purple:
      "border border-purple-8 dark:border-purpledark-8 hover:border-purple-9 dark:hover:border-purpledark-9 bg-purple-subtle",
    teal: "border border-teal-8 dark:border-tealdark-8 hover:border-teal-9 dark:hover:border-tealdark-9 bg-teal-subtle",
    pink: "border border-pink-8 dark:border-pinkdark-8 hover:border-pink-9 dark:hover:border-pinkdark-9 bg-pink-subtle",
    plum: "border border-plum-8 dark:border-plumdark-8 hover:border-plum-9 dark:hover:border-plumdark-9 bg-plum-subtle",
    brown:
      "border border-brown-8 dark:border-browndark-8 hover:border-brown-9 dark:hover:border-browndark-9 bg-brown-subtle",
    tomato:
      "border border-tomato-8 dark:border-tomatodark-8 hover:border-tomato-9 dark:hover:border-tomatodark-9 bg-tomato-subtle",
    ruby: "border border-ruby-8 dark:border-rubydark-8 hover:border-ruby-9 dark:hover:border-rubydark-9 bg-ruby-subtle",
    crimson:
      "border border-crimson-8 dark:border-crimsondark-8 hover:border-crimson-9 dark:hover:border-crimsondark-9 bg-crimson-subtle",
    violet:
      "border border-violet-8 dark:border-violetdark-8 hover:border-violet-9 dark:hover:border-violetdark-9 bg-violet-subtle",
    iris: "border border-iris-8 dark:border-irisdark-8 hover:border-iris-9 dark:hover:border-irisdark-9 bg-iris-subtle",
    indigo:
      "border border-indigo-8 dark:border-indigodark-8 hover:border-indigo-9 dark:hover:border-indigodark-9 bg-indigo-subtle",
    cyan: "border border-cyan-8 dark:border-cyandark-8 hover:border-cyan-9 dark:hover:border-cyandark-9 bg-cyan-subtle",
    jade: "border border-jade-8 dark:border-jadedark-8 hover:border-jade-9 dark:hover:border-jadedark-9 bg-jade-subtle",
    grass:
      "border border-grass-8 dark:border-grassdark-8 hover:border-grass-9 dark:hover:border-grassdark-9 bg-grass-subtle",
    bronze:
      "border border-bronze-8 dark:border-bronzedark-8 hover:border-bronze-9 dark:hover:border-bronzedark-9 bg-bronze-subtle",
    gold: "border border-gold-8 dark:border-golddark-8 hover:border-gold-9 dark:hover:border-golddark-9 bg-gold-subtle",
    orange:
      "border border-orange-8 dark:border-orangedark-8 hover:border-orange-9 dark:hover:border-orangedark-9 bg-orange-subtle",

    amber:
      "border border-amber-8 dark:border-amberdark-8 hover:border-amber-9 dark:hover:border-amberdark-9 bg-amber-subtle",
    yellow:
      "border border-yellow-8 dark:border-yellowdark-8 hover:border-yellow-9 dark:hover:border-yellowdark-9 bg-yellow-subtle",
    lime: "border border-lime-8 dark:border-limedark-8 hover:border-lime-9 dark:hover:border-limedark-9 bg-lime-subtle",
    mint: "border border-mint-8 dark:border-mintdark-8 hover:border-mint-9 dark:hover:border-mintdark-9 bg-mint-subtle",
    sky: "border border-sky-8 dark:border-skydark-8 hover:border-sky-9 dark:hover:border-skydark-9 bg-sky-subtle",
  },
  checkbox: {
    default:
      "data-[state=checked]:bg-slate-solid data-[state=checked]:text-slate-0 dark:data-[state=checked]:text-slatedark-0",
    green:
      "data-[state=checked]:bg-green-solid data-[state=checked]:text-green-0 dark:data-[state=checked]:text-greendark-0",
    red: "data-[state=checked]:bg-red-solid data-[state=checked]:text-red-0 dark:data-[state=checked]:text-reddark-0",
    blue: "data-[state=checked]:bg-blue-solid data-[state=checked]:text-blue-0 dark:data-[state=checked]:text-bluedark-0",
    purple:
      "data-[state=checked]:bg-purple-solid data-[state=checked]:text-purple-0 dark:data-[state=checked]:text-purpledark-0",
    teal: "data-[state=checked]:bg-teal-solid data-[state=checked]:text-teal-0 dark:data-[state=checked]:text-tealdark-0",
    pink: "data-[state=checked]:bg-pink-solid data-[state=checked]:text-pink-0 dark:data-[state=checked]:text-pinkdark-0",
    plum: "data-[state=checked]:bg-plum-solid data-[state=checked]:text-plum-0 dark:data-[state=checked]:text-plumdark-0",
    brown:
      "data-[state=checked]:bg-brown-solid data-[state=checked]:text-brown-0 dark:data-[state=checked]:text-browndark-0",
    tomato:
      "data-[state=checked]:bg-tomato-solid data-[state=checked]:text-tomato-0 dark:data-[state=checked]:text-tomatodark-0",
    ruby: "data-[state=checked]:bg-ruby-solid data-[state=checked]:text-ruby-0 dark:data-[state=checked]:text-rubydark-0",
    crimson:
      "data-[state=checked]:bg-crimson-solid data-[state=checked]:text-crimson-0 dark:data-[state=checked]:text-crimsondark-0",
    violet:
      "data-[state=checked]:bg-violet-solid data-[state=checked]:text-violet-0 dark:data-[state=checked]:text-violetdark-0",
    iris: "data-[state=checked]:bg-iris-solid data-[state=checked]:text-iris-0 dark:data-[state=checked]:text-irisdark-0",
    indigo:
      "data-[state=checked]:bg-indigo-solid data-[state=checked]:text-indigo-0 dark:data-[state=checked]:text-indigodark-0",
    cyan: "data-[state=checked]:bg-cyan-solid data-[state=checked]:text-cyan-0 dark:data-[state=checked]:text-cyandark-0",
    jade: "data-[state=checked]:bg-jade-solid data-[state=checked]:text-jade-0 dark:data-[state=checked]:text-jadedark-0",
    grass:
      "data-[state=checked]:bg-grass-solid data-[state=checked]:text-grass-0 dark:data-[state=checked]:text-grassdark-0",
    bronze:
      "data-[state=checked]:bg-bronze-solid data-[state=checked]:text-bronze-0 dark:data-[state=checked]:text-bronzedark-0",
    gold: "data-[state=checked]:bg-gold-solid data-[state=checked]:text-gold-0 dark:data-[state=checked]:text-golddark-0",
    orange:
      "data-[state=checked]:bg-orange-solid data-[state=checked]:text-orange-0 dark:data-[state=checked]:text-orangedark-0",
    amber:
      "data-[state=checked]:bg-amber-solid data-[state=checked]:text-amber-0 dark:data-[state=checked]:text-amberdark-0",
    yellow:
      "data-[state=checked]:bg-yellow-solid data-[state=checked]:text-yellow-0 dark:data-[state=checked]:text-yellowdark-0",
    lime: "data-[state=checked]:bg-lime-solid data-[state=checked]:text-lime-0 dark:data-[state=checked]:text-limedark-0",
    mint: "data-[state=checked]:bg-mint-solid data-[state=checked]:text-mint-0 dark:data-[state=checked]:text-mintdark-0",
    sky: "data-[state=checked]:bg-sky-solid data-[state=checked]:text-sky-0 dark:data-[state=checked]:text-skydark-0",
  },
  button: {
    default:
      "text-slate-normal border border-slate-normal bg-slate-4 dark:bg-slatedark-4 hover:bg-slate-3 dark:hover:bg-slatedark-3 data-[state=on]:bg-slate-3 data-[state=on]:bg-slatedark-3",
    green:
      "text-green-normal border border-green-normal bg-green-4 dark:bg-greendark-4 hover:bg-green-3 dark:hover:bg-greendark-3 data-[state=on]:bg-green-3 data-[state=on]:bg-greendark-3",
    red: "text-red-normal border border-red-normal bg-red-4 dark:bg-reddark-4 hover:bg-red-3 dark:hover:bg-reddark-3 data-[state=on]:bg-red-3 data-[state=on]:bg-reddark-3",
    blue: "text-blue-normal border border-blue-normal bg-blue-4 dark:bg-bluedark-4 hover:bg-blue-3 dark:hover:bg-bluedark-3 data-[state=on]:bg-blue-3 data-[state=on]:bg-bluedark-3",
    purple:
      "text-purple-normal border border-purple-normal bg-purple-4 dark:bg-purpledark-4 hover:bg-purple-3 dark:hover:bg-purpledark-3 data-[state=on]:bg-purple-3 data-[state=on]:bg-purpledark-3",
    teal: "text-teal-normal border border-teal-normal bg-teal-4 dark:bg-tealdark-4 hover:bg-teal-3 dark:hover:bg-tealdark-3 data-[state=on]:bg-teal-3 data-[state=on]:bg-tealdark-3",
    pink: "text-pink-normal border border-pink-normal bg-pink-4 dark:bg-pinkdark-4 hover:bg-pink-3 dark:hover:bg-pinkdark-3 data-[state=on]:bg-pink-3 data-[state=on]:bg-pinkdark-3",
    amber:
      "text-amber-normal border border-amber-normal bg-amber-4 dark:bg-amberdark-4 hover:bg-amber-3 dark:hover:bg-amberdark-3 data-[state=on]:bg-amber-3 data-[state=on]:bg-amberdark-3",
    plum: "text-plum-normal border border-plum-normal bg-plum-4 dark:bg-plumdark-4 hover:bg-plum-3 dark:hover:bg-plumdark-3 data-[state=on]:bg-plum-3 data-[state=on]:bg-plumdark-3",
    brown:
      "text-brown-normal border border-brown-normal bg-brown-4 dark:bg-browndark-4 hover:bg-brown-3 dark:hover:bg-browndark-3 data-[state=on]:bg-brown-3 data-[state=on]:bg-browndark-3",
    tomato:
      "text-tomato-normal border border-tomato-normal bg-tomato-4 dark:bg-tomatodark-4 hover:bg-tomato-3 dark:hover:bg-tomatodark-3 data-[state=on]:bg-tomato-3 data-[state=on]:bg-tomatodark-3",
    ruby: "text-ruby-normal border border-ruby-normal bg-ruby-4 dark:bg-rubydark-4 hover:bg-ruby-3 dark:hover:bg-rubydark-3 data-[state=on]:bg-ruby-3 data-[state=on]:bg-rubydark-3",
    crimson:
      "text-crimson-normal border border-crimson-normal bg-crimson-4 dark:bg-crimsondark-4 hover:bg-crimson-3 dark:hover:bg-crimsondark-3 data-[state=on]:bg-crimson-3 data-[state=on]:bg-crimsondark-3",
    violet:
      "text-violet-normal border border-violet-normal bg-violet-4 dark:bg-violetdark-4 hover:bg-violet-3 dark:hover:bg-violetdark-3 data-[state=on]:bg-violet-3 data-[state=on]:bg-violetdark-3",
    iris: "text-iris-normal border border-iris-normal bg-iris-4 dark:bg-irisdark-4 hover:bg-iris-3 dark:hover:bg-irisdark-3 data-[state=on]:bg-iris-3 data-[state=on]:bg-irisdark-3",
    indigo:
      "text-indigo-normal border border-indigo-normal bg-indigo-4 dark:bg-indigodark-4 hover:bg-indigo-3 dark:hover:bg-indigodark-3 data-[state=on]:bg-indigo-3 data-[state=on]:bg-indigodark-3",
    cyan: "text-cyan-normal border border-cyan-normal bg-cyan-4 dark:bg-cyandark-4 hover:bg-cyan-3 dark:hover:bg-cyandark-3 data-[state=on]:bg-cyan-3 data-[state=on]:bg-cyandark-3",
    jade: "text-jade-normal border border-jade-normal bg-jade-4 dark:bg-jadedark-4 hover:bg-jade-3 dark:hover:bg-jadedark-3 data-[state=on]:bg-jade-3 data-[state=on]:bg-jadedark-3",
    grass:
      "text-grass-normal border border-grass-normal bg-grass-4 dark:bg-grassdark-4 hover:bg-grass-3 dark:hover:bg-grassdark-3 data-[state=on]:bg-grass-3 data-[state=on]:bg-grassdark-3",
    bronze:
      "text-bronze-normal border border-bronze-normal bg-bronze-4 dark:bg-bronzedark-4 hover:bg-bronze-3 dark:hover:bg-bronzedark-3 data-[state=on]:bg-bronze-3 data-[state=on]:bg-bronzedark-3",
    gold: "text-gold-normal border border-gold-normal bg-gold-4 dark:bg-golddark-4 hover:bg-gold-3 dark:hover:bg-golddark-3 data-[state=on]:bg-gold-3 data-[state=on]:bg-golddark-3",
    orange:
      "text-orange-normal border border-orange-normal bg-orange-4 dark:bg-orangedark-4 hover:bg-orange-3 dark:hover:bg-orangedark-3 data-[state=on]:bg-orange-3 data-[state=on]:bg-orangedark-3",
    yellow:
      "text-yellow-normal border border-yellow-normal bg-yellow-4 dark:bg-yellowdark-4 hover:bg-yellow-3 dark:hover:bg-yellowdark-3 data-[state=on]:bg-yellow-3 data-[state=on]:bg-yellowdark-3",
    lime: "text-lime-normal border border-lime-normal bg-lime-4 dark:bg-limedark-4 hover:bg-lime-3 dark:hover:bg-limedark-3 data-[state=on]:bg-lime-3 data-[state=on]:bg-limedark-3",
    mint: "text-mint-normal border border-mint-normal bg-mint-4 dark:bg-mintdark-4 hover:bg-mint-3 dark:hover:bg-mintdark-3 data-[state=on]:bg-mint-3 data-[state=on]:bg-mintdark-3",
    sky: "text-sky-normal border border-sky-normal bg-sky-4 dark:bg-skydark-4 hover:bg-sky-3 dark:hover:bg-skydark-3 data-[state=on]:bg-sky-3 data-[state=on]:bg-skydark-3",
  },
  separator: {
    default: "bg-slate-8 dark:bg-slatedark-8",
    green: "bg-green-8 dark:bg-greendark-8",
    red: "bg-red-8 dark:bg-reddark-8",
    blue: "bg-blue-8 dark:bg-bluedark-8",
    purple: "bg-purple-8 dark:bg-purpledark-8",
    teal: "bg-teal-8 dark:bg-tealdark-8",
    pink: "bg-pink-8 dark:bg-pinkdark-8",
    amber: "bg-amber-8 dark:bg-amberdark-8",
    plum: "bg-plum-8 dark:bg-plumdark-8",
    brown: "bg-brown-8 dark:bg-browndark-8",
    tomato: "bg-tomato-8 dark:bg-tomatodark-8",
    ruby: "bg-ruby-8 dark:bg-rubydark-8",
    crimson: "bg-crimson-8 dark:bg-crimsondark-8",
    violet: "bg-violet-8 dark:bg-violetdark-8",
    iris: "bg-iris-8 dark:bg-irisdark-8",
    indigo: "bg-indigo-8 dark:bg-indigodark-8",
    cyan: "bg-cyan-8 dark:bg-cyandark-8",
    jade: "bg-jade-8 dark:bg-jadedark-8",
    grass: "bg-grass-8 dark:bg-grassdark-8",
    bronze: "bg-bronze-8 dark:bg-bronzedark-8",
    gold: "bg-gold-8 dark:bg-golddark-8",
    orange: "bg-orange-8 dark:bg-orangedark-8",
    yellow: "bg-yellow-8 dark:bg-yellowdark-8",
    lime: "bg-lime-8 dark:bg-limedark-8",
    mint: "bg-mint-8 dark:bg-mintdark-8",
    sky: "bg-sky-8 dark:bg-skydark-8",
  },
  numberInputButton: {
    default: "bg-slate-action",
    green: "bg-green-action",
    red: "bg-red-action",
    blue: "bg-blue-action",
    purple: "bg-purple-action",
    teal: "bg-teal-action",
    pink: "bg-pink-action",
    amber: "bg-amber-action",
    plum: "bg-plum-action",
    brown: "bg-brown-action",
    tomato: "bg-tomato-action",
    ruby: "bg-ruby-action",
    crimson: "bg-crimson-action",
    violet: "bg-violet-action",
    iris: "bg-iris-action",
    indigo: "bg-indigo-action",
    cyan: "bg-cyan-action",
    jade: "bg-jade-action",
    grass: "bg-grass-action",
    bronze: "bg-bronze-action",
    gold: "bg-gold-action",
    orange: "bg-orange-action",
    yellow: "bg-yellow-action",
    lime: "bg-lime-action",
    mint: "bg-mint-action",
    sky: "bg-sky-action",
  },
  numberInputBorder: {
    default: "border-slate-8 dark:border-slatedark-8",
    green: "border-green-8 dark:border-greendark-8",
    red: "border-red-8 dark:border-reddark-8",
    blue: "border-blue-8 dark:border-bluedark-8",
    purple: "border-purple-8 dark:border-purpledark-8",
    teal: "border-teal-8 dark:border-tealdark-8",
    pink: "border-pink-8 dark:border-pinkdark-8",
    plum: "border-plum-8 dark:border-plumdark-8",
    brown: "border-brown-8 dark:border-browndark-8",
    tomato: "border-tomato-8 dark:border-tomatodark-8",
    ruby: "border-ruby-8 dark:border-rubydark-8",
    crimson: "border-crimson-8 dark:border-crimsondark-8",
    violet: "border-violet-8 dark:border-violetdark-8",
    iris: "border-iris-8 dark:border-irisdark-8",
    indigo: "border-indigo-8 dark:border-indigodark-8",
    cyan: "border-cyan-8 dark:border-cyandark-8",
    jade: "border-jade-8 dark:border-jadedark-8",
    grass: "border-grass-8 dark:border-grassdark-8",
    bronze: "border-bronze-8 dark:border-bronzedark-8",
    gold: "border-gold-8 dark:border-golddark-8",
    orange: "border-orange-8 dark:border-orangedark-8",

    amber: "border-amber-8 dark:border-amberdark-8",
    yellow: "border-yellow-8 dark:border-yellowdark-8",
    lime: "border-lime-8 dark:border-limedark-8",
    mint: "border-mint-8 dark:border-mintdark-8",
    sky: "border-sky-8 dark:border-skydark-8",
  },
  nodeCursor: {
    default: "bg-slate-action text-slate-dim border-slate-normal",
    green: "bg-green-action text-green-dim border-green-normal",
    red: "bg-red-action text-red-dim border-red-normal",
    blue: "bg-blue-action text-blue-dim border-blue-normal",
    purple: "bg-purple-action text-purple-dim border-purple-normal",
    teal: "bg-teal-action text-teal-dim border-teal-normal",
    pink: "bg-pink-action text-pink-dim border-pink-normal",
    amber: "bg-amber-action text-amber-dim border-amber-normal",
    plum: "bg-plum-action text-plum-dim border-plum-normal",
    brown: "bg-brown-action text-brown-dim border-brown-normal",
    tomato: "bg-tomato-action text-tomato-dim border-tomato-normal",
    ruby: "bg-ruby-action text-ruby-dim border-ruby-normal",
    crimson: "bg-crimson-action text-crimson-dim border-crimson-normal",
    violet: "bg-violet-action text-violet-dim border-violet-normal",
    iris: "bg-iris-action text-iris-dim border-iris-normal",
    indigo: "bg-indigo-action text-indigo-dim border-indigo-normal",
    cyan: "bg-cyan-action text-cyan-dim border-cyan-normal",
    jade: "bg-jade-action text-jade-dim border-jade-normal",
    grass: "bg-grass-action text-grass-dim border-grass-normal",
    bronze: "bg-bronze-action text-bronze-dim border-bronze-normal",
    gold: "bg-gold-action text-gold-dim border-gold-normal",
    orange: "bg-orange-action text-orange-dim border-orange-normal",
    yellow: "bg-yellow-action text-yellow-dim border-yellow-normal",
    lime: "bg-lime-action text-lime-dim border-lime-normal",
    mint: "bg-mint-action text-mint-dim border-mint-normal",
    sky: "bg-sky-action text-sky-dim border-sky-normal",
  },
  surface: {
    default:
      "bg-slate-6 dark:bg-slatedark-6 border-slate-8 hover:border-slate-9 dark:border-slatedark-8 dark:hover:border-slatedark-9",
    green:
      "bg-green-6 dark:bg-greendark-6 border-green-8 hover:border-green-9 dark:border-greendark-8 dark:hover:border-greendark-9",
    red: "bg-red-6 dark:bg-reddark-6 border-red-8 hover:border-red-9 dark:border-reddark-8 dark:hover:border-reddark-9",
    blue: "bg-blue-6 dark:bg-bluedark-6 border-blue-8 hover:border-blue-9 dark:border-bluedark-8 dark:hover:border-bluedark-9",
    purple:
      "bg-purple-6 dark:bg-purpledark-6 border-purple-8 hover:border-purple-9 dark:border-purpledark-8 dark:hover:border-purpledark-9",
    teal: "bg-teal-6 dark:bg-tealdark-6 border-teal-8 hover:border-teal-9 dark:border-tealdark-8 dark:hover:border-tealdark-9",
    pink: "bg-pink-6 dark:bg-pinkdark-6 border-pink-8 hover:border-pink-9 dark:border-pinkdark-8 dark:hover:border-pinkdark-9",
    plum: "bg-plum-6 dark:bg-plumdark-6 border-plum-8 hover:border-plum-9 dark:border-plumdark-8 dark:hover:border-plumdark-9",
    brown:
      "bg-brown-6 dark:bg-browndark-6 border-brown-8 hover:border-brown-9 dark:border-browndark-8 dark:hover:border-browndark-9",
    tomato:
      "bg-tomato-6 dark:bg-tomatodark-6 border-tomato-8 hover:border-tomato-9 dark:border-tomatodark-8 dark:hover:border-tomatodark-9",
    ruby: "bg-ruby-6 dark:bg-rubydark-6 border-ruby-8 hover:border-ruby-9 dark:border-rubydark-8 dark:hover:border-rubydark-9",
    crimson:
      "bg-crimson-6 dark:bg-crimsondark-6 border-crimson-8 hover:border-crimson-9 dark:border-crimsondark-8 dark:hover:border-crimsondark-9",
    violet:
      "bg-violet-6 dark:bg-violetdark-6 border-violet-8 hover:border-violet-9 dark:border-violetdark-8 dark:hover:border-violetdark-9",
    iris: "bg-iris-6 dark:bg-irisdark-6 border-iris-8 hover:border-iris-9 dark:border-irisdark-8 dark:hover:border-irisdark-9",
    indigo:
      "bg-indigo-6 dark:bg-indigodark-6 border-indigo-8 hover:border-indigo-9 dark:border-indigodark-8 dark:hover:border-indigodark-9",
    cyan: "bg-cyan-6 dark:bg-cyandark-6 border-cyan-8 hover:border-cyan-9 dark:border-cyandark-8 dark:hover:border-cyandark-9",
    jade: "bg-jade-6 dark:bg-jadedark-6 border-jade-8 hover:border-jade-9 dark:border-jadedark-8 dark:hover:border-jadedark-9",
    grass:
      "bg-grass-6 dark:bg-grassdark-6 border-grass-8 hover:border-grass-9 dark:border-grassdark-8 dark:hover:border-grassdark-9",
    bronze:
      "bg-bronze-6 dark:bg-bronzedark-6 border-bronze-8 hover:border-bronze-9 dark:border-bronzedark-8 dark:hover:border-bronzedark-9",
    gold: "bg-gold-6 dark:bg-golddark-6 border-gold-8 hover:border-gold-9 dark:border-golddark-8 dark:hover:border-golddark-9",
    orange:
      "bg-orange-6 dark:bg-orangedark-6 border-orange-8 hover:border-orange-9 dark:border-orangedark-8 dark:hover:border-orangedark-9",

    amber:
      "bg-amber-6 dark:bg-amberdark-6 border-amber-8 hover:border-amber-11 dark:border-amberdark-8 dark:hover:border-amberdark-11",
    yellow:
      "bg-yellow-6 dark:bg-yellowdark-6 border-yellow-8 hover:border-yellow-11 dark:border-yellowdark-8 dark:hover:border-yellowdark-11",
    lime: "bg-lime-6 dark:bg-limedark-6 border-lime-8 hover:border-lime-11 dark:border-limedark-8 dark:hover:border-limedark-11",
    mint: "bg-mint-6 dark:bg-mintdark-6 border-mint-8 hover:border-mint-11 dark:border-mintdark-8 dark:hover:border-mintdark-11",
    sky: "bg-sky-6 dark:bg-skydark-6 border-sky-8 hover:border-sky-11 dark:border-skydark-8 dark:hover:border-skydark-11",
  },
  header: {
    default:
      "bg-slate-7 dark:bg-slatedark-7 text-slate-normal border-slate-8 dark:border-slatedark-8",
    green:
      "bg-green-7 dark:bg-greendark-7 text-green-normal border-green-8 dark:border-greendark-8",
    red: "bg-red-7 dark:bg-reddark-7 text-red-normal border-red-8 dark:border-reddark-8",
    blue: "bg-blue-7 dark:bg-bluedark-7 text-blue-normal border-blue-8 dark:border-bluedark-8",
    purple:
      "bg-purple-7 dark:bg-purpledark-7 text-purple-normal border-purple-8 dark:border-purpledark-8",
    teal: "bg-teal-7 dark:bg-tealdark-7 text-teal-normal border-teal-8 dark:border-tealdark-8",
    pink: "bg-pink-7 dark:bg-pinkdark-7 text-pink-normal border-pink-8 dark:border-pinkdark-8",
    amber:
      "bg-amber-7 dark:bg-amberdark-7 text-amber-normal border-amber-8 dark:border-amberdark-8",
    plum: "bg-plum-7 dark:bg-plumdark-7 text-plum-normal border-plum-8 dark:border-plumdark-8",
    brown:
      "bg-brown-7 dark:bg-browndark-7 text-brown-normal border-brown-8 dark:border-browndark-8",
    tomato:
      "bg-tomato-7 dark:bg-tomatodark-7 text-tomato-normal border-tomato-8 dark:border-tomatodark-8",
    ruby: "bg-ruby-7 dark:bg-rubydark-7 text-ruby-normal border-ruby-8 dark:border-rubydark-8",
    crimson:
      "bg-crimson-7 dark:bg-crimsondark-7 text-crimson-normal border-crimson-8 dark:border-crimsondark-8",
    violet:
      "bg-violet-7 dark:bg-violetdark-7 text-violet-normal border-violet-8 dark:border-violetdark-8",
    iris: "bg-iris-7 dark:bg-irisdark-7 text-iris-normal border-iris-8 dark:border-irisdark-8",
    indigo:
      "bg-indigo-7 dark:bg-indigodark-7 text-indigo-normal border-indigo-8 dark:border-indigodark-8",
    cyan: "bg-cyan-7 dark:bg-cyandark-7 text-cyan-normal border-cyan-8 dark:border-cyandark-8",
    jade: "bg-jade-7 dark:bg-jadedark-7 text-jade-normal border-jade-8 dark:border-jadedark-8",
    grass:
      "bg-grass-7 dark:bg-grassdark-7 text-grass-normal border-grass-8 dark:border-grassdark-8",
    bronze:
      "bg-bronze-7 dark:bg-bronzedark-7 text-bronze-normal border-bronze-8 dark:border-bronzedark-8",
    gold: "bg-gold-7 dark:bg-golddark-7 text-gold-normal border-gold-8 dark:border-golddark-8",
    orange:
      "bg-orange-7 dark:bg-orangedark-7 text-orange-normal border-orange-8 dark:border-orangedark-8",
    yellow:
      "bg-yellow-7 dark:bg-yellowdark-7 text-yellow-normal border-yellow-8 dark:border-yellowdark-8",
    lime: "bg-lime-7 dark:bg-limedark-7 text-lime-normal border-lime-8 dark:border-limedark-8",
    mint: "bg-mint-7 dark:bg-mintdark-7 text-mint-normal border-mint-8 dark:border-mintdark-8",
    sky: "bg-sky-7 dark:bg-skydark-7 text-sky-normal border-sky-8 dark:border-skydark-8",
  },
  text: {
    default: "text-slate-normal",
    green: "text-green-normal",
    red: "text-red-normal",
    blue: "text-blue-normal",
    purple: "text-purple-normal",
    teal: "text-teal-normal",
    pink: "text-pink-normal",
    amber: "text-amber-normal",
    yellow: "text-yellow-normal",
    lime: "text-lime-normal",
    mint: "text-mint-normal",
    sky: "text-sky-normal",
    tomato: "text-tomato-normal",
    ruby: "text-ruby-normal",
    crimson: "text-crimson-normal",
    plum: "text-plum-normal",
    violet: "text-violet-normal",
    iris: "text-iris-normal",
    indigo: "text-indigo-normal",
    cyan: "text-cyan-normal",
    jade: "text-jade-normal",
    grass: "text-grass-normal",
    bronze: "text-bronze-normal",
    gold: "text-gold-normal",
    brown: "text-brown-normal",
    orange: "text-orange-normal",
  },
} satisfies Record<string, Record<BaseVariant, string>>;

const containerVariants = cva("flex flex-col rounded-lg border-2");

export const colorVariants = [
  "slate",
  "tomato",
  "red",
  "ruby",
  "crimson",
  "pink",
  "plum",
  "purple",
  "violet",
  "iris",
  "indigo",
  "blue",
  "cyan",
  "teal",
  "jade",
  "green",
  "grass",
  "bronze",
  "gold",
  "brown",
  "orange",
  "amber",
  "yellow",
  "lime",
  "mint",
  "sky",
] as const;

export type BaseVariant =
  | Exclude<(typeof colorVariants)[number], "slate">
  | "default";
export type ColorVariant = BaseVariant | "slate";

export const ColorContext = createContext<BaseVariant | null>(null);

interface BlockContainerProps extends React.ComponentProps<"div"> {
  height?: number;
  width?: number;
  isBound?: boolean;
  dynamicHeight?: boolean;
  onUpdateDimensions?: (dimensions: Pick<Dimensions, "h">) => void;
  asDiv?: boolean;
  resultState?: "success" | "failure" | "idle";
  isSelected?: boolean;
  variant?: ColorVariant;
  pattern?: Pattern;
  hideChildren?: boolean;
  zoomPreview?: boolean;
}

export const BlockContainerShapeContext = createContext<
  BindableShape | undefined
>(undefined);

export const LazyRenderBlocksContext = createContext<boolean>(false);
const HideBodyContext = createContext<boolean>(false);
export const ZoomLabelContext = createContext<boolean>(false);

export const BlockContainerBase = function BlockContainerBase({
  ref,
  children,
  className,
  variant: variantProp,
  height,
  width,
  isBound = true,
  onUpdateDimensions,
  dynamicHeight,
  asDiv,
  resultState,
  isSelected,
  pattern: patternProp,
  hideChildren = false,
  zoomPreview = false,
  ...props
}: BlockContainerProps) {
  const innerRef = useRef<HTMLDivElement>(null);
  const mergedRef = useComposedRefs(ref, innerRef);
  const isReadOnly = use(ReadonlyEditorContext);
  if (variantProp === "slate") {
    variantProp = "default";
  }

  const variant =
    resultState === "success"
      ? "green"
      : resultState === "failure"
      ? "red"
      : variantProp;
  const pattern =
    patternProp && resultState === "success"
      ? "success"
      : patternProp && resultState === "failure"
      ? "error"
      : patternProp;

  const callFn = useCallback(
    (dimensions: Pick<Dimensions, "h">) => {
      onUpdateDimensions?.(dimensions);
    },
    [onUpdateDimensions],
  );
  const debouncedOnUpdateDimensions = useThrottledCallback(callFn, 50, {});

  useSharedResizeObserver({
    ref: innerRef,
    onUpdate: (entry) => {
      const box = entry.borderBoxSize[0];
      if (!box) return;
      const { blockSize } = box;
      if (!blockSize || hideChildren) return;
      debouncedOnUpdateDimensions({ h: blockSize });
    },
  });

  const RootComponent = asDiv ? "div" : HTMLContainer;

  const isMobile = useIsMobile();
  const inFrontOfCanvasPortalRef = use(InFrontOfCanvasPortalContext);

  return (
    <ColorContext value={variant || "default"}>
      <RootComponent
        {...props}
        className={cn(className, !isBound && "bg-slate-app rounded-lg")}
        style={{ height, width }}
        onPointerDownCapture={(e) => {
          if (isReadOnly) {
            const isInTooltip =
              (e.target instanceof HTMLElement ||
                e.target instanceof SVGElement) &&
              (e.target.closest("[data-tooltip-trigger]") ||
                e.target.getAttribute("data-tooltip-trigger"));

            if (isInTooltip) {
              return;
            }

            e.stopPropagation();
          }
        }}
        onMouseDownCapture={(e) => {
          if (isReadOnly) {
            const isInTooltip =
              (e.target instanceof HTMLElement ||
                e.target instanceof SVGElement) &&
              (e.target.closest("[data-tooltip-trigger]") ||
                e.target.getAttribute("data-tooltip-trigger"));

            if (isInTooltip) {
              return;
            }

            e.stopPropagation();
          }
        }}
      >
        {isSelected && isMobile && !isReadOnly && (
          <Portal
            container={inFrontOfCanvasPortalRef.el}
            data-radix-popper-content-wrapper
            data-drag-handle
            {...mergeProps(props, preventBubble)}
            className="h-full"
          >
            <button
              type="button"
              className="h-full bg-slate-ui border border-slate-normal w-16 rounded flex items-center justify-center"
            >
              <DragHandleDots2Icon className="w-6 h-6" />
            </button>
          </Portal>
        )}
        <div
          ref={mergedRef}
          className={cn(
            !dynamicHeight && "min-h-full min-w-full",
            !isBound && "opacity-65",
            hideChildren && "h-full",
            containerVariants(),
            theme.surface[variant || "default"],
            pattern === "lines" &&
              "before:rounded-lg before:content-[''] before:absolute before:inset-0 before:bg-[repeating-linear-gradient(45deg,transparent,transparent_10px,var(--node-pattern-color)_10px,var(--node-pattern-color)_12px)]",
            pattern === "dots" &&
              "before:rounded-lg before:content-[''] before:absolute before:inset-0 before:bg-[radial-gradient(circle_at_5px_5px,var(--node-pattern-color)_3px,transparent_2px,transparent_15px)] before:bg-size-[10px_10px]",
            pattern === "checkerboard" &&
              "before:rounded-lg before:content-[''] before:absolute before:inset-0 before:bg-[linear-gradient(45deg,var(--node-pattern-color)_25%,transparent_25%),linear-gradient(-45deg,var(--node-pattern-color)_25%,transparent_25%),linear-gradient(45deg,transparent_75%,var(--node-pattern-color)_75%),linear-gradient(-45deg,transparent_75%,var(--node-pattern-color)_75%)] before:bg-size-[16px_16px] before:bg-[postition:0_0,0_8px,8px_-8px,-8px_0px]",
            pattern === "zig zag" &&
              "before:rounded-lg before:content-[''] before:absolute before:inset-0 before:bg-[linear-gradient(135deg,var(--node-pattern-color)_25%,transparent_25%),linear-gradient(225deg,var(--node-pattern-color)_25%,transparent_25%),linear-gradient(315deg,var(--node-pattern-color)_25%,transparent_25%),linear-gradient(45deg,var(--node-pattern-color)_25%,transparent_25%)] before:bg-size-[16px_16px] before:bg-position-[-8px_0,-8px_0,0_0,0_0]",
            pattern === "cow" &&
              "before:rounded-lg before:content-[''] before:absolute before:inset-0 cow-pattern",
            pattern === "success" &&
              "before:rounded-lg before:content-[''] before:absolute before:inset-0 success-pattern",
            pattern === "error" &&
              "before:rounded-lg before:content-[''] before:absolute before:inset-0 error-pattern",
          )}
        >
          <ZoomLabelContext value={zoomPreview}>
            <HideBodyContext value={hideChildren}>{children}</HideBodyContext>
          </ZoomLabelContext>
        </div>
      </RootComponent>
    </ColorContext>
  );
};

export function getHasHiddenParent(editor: Editor, shape: BindableShape) {
  let currentShape = shape;

  while (currentShape?.parentId) {
    const parent = editor.getShape<BindableShape>(currentShape.parentId);

    if (!parent) break;
    if (parent?.props.isCollapsed) return true;
    currentShape = parent;
  }

  return false;
}

export function BlockContainer(props: BlockContainerProps) {
  const shape = use(BlockContainerShapeContext);
  const editor = useEditor();
  const ref = useRef<HTMLDivElement>(null);
  const activeShape = useRef<BindableShape | undefined>(undefined);
  const activeUtil = useRef<BindableUtil | undefined>(undefined);
  const { moveProps } = useMove({
    onMoveStart() {
      const currentShape = editor.getSelectedShapes();

      if (currentShape.length === 1) {
        activeShape.current = currentShape[0] as BindableShape;
        const util = editor.getShapeUtil<BindableUtil>(activeShape.current);
        util.onTranslateStart(activeShape.current);
        activeUtil.current = util;
      }
    },
    onMove(e) {
      if (!activeShape.current) {
        return;
      }

      const newShape = {
        ...activeShape.current,
        x: activeShape.current.x + e.deltaX / editor.getZoomLevel(),
        y: activeShape.current.y + e.deltaY / editor.getZoomLevel(),
      };

      activeUtil.current?.onTranslate(
        activeShape.current,
        newShape,
        new Vec(200, 0),
      );
      activeShape.current = newShape;
      editor.updateShape(newShape);
    },
    onMoveEnd() {
      if (!activeShape.current) {
        return;
      }

      activeUtil.current?.onTranslateEnd(
        activeShape.current,
        activeShape.current,
        new Vec(200, 0),
      );

      activeShape.current = undefined;
      activeUtil.current = undefined;
    },
  });

  const hasHiddenParent = shape && getHasHiddenParent(editor, shape);

  const innerRef = useRef<HTMLDivElement>(null);
  const mergedRef = useComposedRefs(ref, innerRef);
  const lazyRender = use(LazyRenderBlocksContext);
  const [isShapeVisible, setIsShapeVisible] = useState(
    lazyRender ? false : true,
  );
  const [isZoomPreview, setIsZoomPreview] = useState(false);

  useEffect(() => {
    if (!shape || !lazyRender) return;

    const THRESHOLD = 500;

    function cornerIsInBox(corner: Vec, box: Box) {
      return (
        corner.x >= box.x - THRESHOLD &&
        corner.x <= box.x + box.width + THRESHOLD &&
        corner.y >= box.y - THRESHOLD &&
        corner.y <= box.y + box.height + THRESHOLD
      );
    }

    function edgeXIsInBox(edge: Box, box: Box) {
      return (
        ((edge.x >= box.x - THRESHOLD &&
          edge.x <= box.x + box.width + THRESHOLD) ||
          (edge.x + edge.width >= box.x - THRESHOLD &&
            edge.x + edge.width <= box.x + box.width + THRESHOLD)) &&
        edge.y <= box.y - THRESHOLD &&
        edge.y + edge.height >= box.y + box.height + THRESHOLD
      );
    }

    function edgeYIsInBox(edge: Box, box: Box) {
      return (
        edge.x <= box.x - THRESHOLD &&
        edge.x + edge.width >= box.x + box.width + THRESHOLD &&
        ((edge.y >= box.y - THRESHOLD &&
          edge.y <= box.y + box.height + THRESHOLD) ||
          (edge.y + edge.height >= box.y - THRESHOLD &&
            edge.y + edge.height <= box.y + box.height + THRESHOLD))
      );
    }

    editor.on("change", () => {
      const viewport = editor.getViewportPageBounds();
      const shapeBound = editor.getShapePageBounds(shape);

      if (!shapeBound) return;

      const zoom = editor.getZoomLevel();

      if (zoom < 0.2) {
        setIsShapeVisible(false);
        setIsZoomPreview(true);
        return;
      }

      const isVisible =
        shapeBound.corners.some((c) => cornerIsInBox(c, viewport)) ||
        edgeXIsInBox(shapeBound, viewport) ||
        edgeYIsInBox(shapeBound, viewport);

      setIsShapeVisible(isVisible);
      setIsZoomPreview(false);
    });
  }, [editor, lazyRender, shape]);

  if (hasHiddenParent) {
    return null;
  }

  const container = (
    <BlockContainerBase
      hideChildren={!isShapeVisible}
      zoomPreview={isZoomPreview}
      ref={mergedRef}
      data-shape-id={shape?.id}
      {...mergeProps(props, moveProps)}
    />
  );

  if (shape && "isReadOnly" in shape.props && shape.props.isReadOnly) {
    return (
      <ReadonlyEditorContext value={true}>{container}</ReadonlyEditorContext>
    );
  }

  return container;
}

const headerVariants = cva(
  "text-xs font-mono uppercase pt-[5px] pb-[5px] h-8 px-3 border-b flex gap-2 items-center justify-between rounded-t-[7px]",
);

export function BlockHeader({
  children,
  description,
  descriptionVariant = "normal",
  variant: variantProp = "default",
}: {
  children: React.ReactNode;
  description?: React.ReactNode;
  descriptionVariant?: "emphasis" | "normal";
  variant?: ColorVariant;
}) {
  const variant = use(ColorContext) || variantProp;
  const showZoom = use(ZoomLabelContext);

  if (showZoom) {
    return children;
  }

  return (
    <div
      className={cn(
        theme.header[variant === "slate" ? "default" : variant],
        headerVariants(),
      )}
    >
      <span className="flex items-center flex-1 min-w-0 gap-2">{children}</span>
      {description && (
        <InfoPopover
          label="About this block"
          triggerClassName={cn(
            descriptionVariant === "emphasis"
              ? "opacity-100 bg-slatea-5 dark:bg-slatedarka-5 p-0.5 rounded"
              : "opacity-40 hover:opacity-80",
          )}
          iconClassName={descriptionVariant === "emphasis" ? "h-4 w-4" : "h-4 w-4"}
        >
          {typeof description === "string" ? (
            <MarkdownRenderer>{description}</MarkdownRenderer>
          ) : (
            description
          )}
        </InfoPopover>
      )}
    </div>
  );
}

/**
 * A small (i) that opens an explanation on CLICK and stays until clicked again
 * or dismissed — a hover tooltip only lasted while the mouse was held on a
 * canvas that swallows pointer events. Used by block headers and inline hints
 * alike, so every (i) in the editor behaves the same way.
 */
function InfoPopover({
  children,
  label,
  triggerClassName,
  iconClassName,
}: {
  children: React.ReactNode;
  label: string;
  triggerClassName?: string;
  iconClassName?: string;
}) {
  return (
    <Popover>
      <PopoverTrigger asChild>
        <button
          type="button"
          aria-label={label}
          {...preventBubble}
          className={cn(
            "pointer-events-auto inline-flex shrink-0 items-center justify-center rounded",
            triggerClassName,
          )}
        >
          <InfoCircledIcon className={cn("h-3.5 w-3.5", iconClassName)} />
        </button>
      </PopoverTrigger>
      <PopoverContent
        side="right"
        sideOffset={8}
        className="max-w-sm p-3 text-sm leading-relaxed"
        onPointerDown={(e) => e.stopPropagation()}
      >
        {children}
      </PopoverContent>
    </Popover>
  );
}

/** A small (i) beside a control; click to read, click again to close. */
export function InfoHint({ children, label }: { children: React.ReactNode; label?: string }) {
  return (
    <InfoPopover label={label ?? "More about this"} triggerClassName="opacity-40 hover:opacity-80">
      {children}
    </InfoPopover>
  );
}

export function NodeBlockHeader({
  props,
  docs,
  isDeprecated,
}: {
  props?: {
    metadata?: NodeMetadata | null;
  };
  docs: {
    title: React.ReactNode;
    description?: React.ReactNode;
    variant?: ColorVariant;
  };
  isDeprecated?: boolean;
}) {
  return (
    <BlockHeader
      descriptionVariant={props?.metadata?.description ? "emphasis" : "normal"}
      description={props?.metadata?.description || docs.description}
      variant={docs.variant}
    >
      <BlockTitle title={docs.title} customTitle={props?.metadata?.title} />
      {isDeprecated && (
        <div className="flex items-center gap-2 bg-ambera-3 dark:bg-amberdarka-3 w-fit rounded p-1">
          Deprecated
        </div>
      )}
    </BlockHeader>
  );
}

export function BlockBodyBase({
  children,
  ref,
  className,
}: {
  children: React.ReactNode;
  ref?: React.Ref<HTMLDivElement>;
  className?: string;
}) {
  return (
    <div className={cn("flex gap-2 p-2", className)} ref={ref}>
      {children}
    </div>
  );
}

export function BlockBody({
  children,
  ref,
  className,
}: {
  children: React.ReactNode;
  ref?: React.Ref<HTMLDivElement>;
  className?: string;
}) {
  const hideChildren = use(HideBodyContext);
  const editor = useEditor();
  const shape = use(BlockContainerShapeContext);
  const onExpand = () => {
    if (!shape) return;

    editor.updateShape({
      id: shape.id,
      type: shape.type,
      props: { ...shape.props, isCollapsed: false },
    });
  };

  if (shape?.props.isCollapsed) {
    return (
      <div className="p-1 w-full" ref={ref}>
        <button
          className="flex items-center justify-center gap-2 p-0.5 bg-slatea-ghost pointer-events-auto rounded-b-md w-full"
          type="button"
          {...mergeProps(preventBubble, {
            onClick: onExpand,
          })}
        >
          <span className="sr-only">Expand</span>
          <ChevronDownIcon className="w-4 h-4" />
        </button>
      </div>
    );
  }

  return (
    <BlockBodyBase ref={ref} className={className}>
      {!hideChildren && children}
    </BlockBodyBase>
  );
}

export function BlockSeparator({ className }: { className?: string }) {
  const variant = use(ColorContext);
  return (
    <Separator
      className={cn(theme.separator[variant || "default"], className)}
    />
  );
}

export function ListAttribution({
  avatar,
  name,
  creator,
  children,
}: {
  avatar: string;
  name: string;
  creator: User;
  children?: React.ReactNode;
}) {
  return (
    <div className="flex-1 flex gap-2 items-center">
      <div className="flex gap-2 items-center flex-1 min-w-0">
        <Avatar src={avatar} alt={creator.handle} className="w-9 h-9" />
        <div>
          <div className="font-medium">{name}</div>
          <div className="text-slate-dim">@{creator.handle}</div>
        </div>
      </div>
      {children}
    </div>
  );
}

export function BlockTitle({
  title,
  customTitle,
}: {
  title: React.ReactNode;
  customTitle?: string;
}) {
  const showZoom = use(ZoomLabelContext);

  if (showZoom) {
    return (
      <span className="text-center w-full text-6xl absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2">
        {title}
      </span>
    );
  }

  if (customTitle) {
    return (
      <div className="flex-1 min-w-0 flex items-center gap-2">
        <span className="min-w-0 truncate">{customTitle}</span>
        <span className="shrink-0 px-2 py-0.5 bg-slatea-5 rounded text-xs">
          {!showZoom && title}
        </span>
      </div>
    );
  }

  return <span>{title}</span>;
}
